import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import classes from "./RightSidebar.module.css";

const normalizeName = (value) =>
    String(value || "").trim().toLowerCase().replace(/\s+/g, " ");

const formatNumber = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "";
    return new Intl.NumberFormat("en-US").format(numericValue);
};

const formatPercent = (value, total) => {
    if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return "";
    const percent = (value / total) * 100;
    return `${percent >= 10 ? percent.toFixed(0) : percent.toFixed(1)}%`;
};

const toNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const parsed = Number(String(value ?? "").replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
};

const getRound = (row) => {
    const value = row?.round_number ?? row?.round ?? row?.Round ?? row?.rounds;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : value;
};

const resolveTargetRound = (rows, roundFilter, selectedRound) => {
    const rounds = (rows || [])
        .map(getRound)
        .map((round) => Number(round))
        .filter((round) => Number.isFinite(round));
    const requestedRound = roundFilter == null ? Number(selectedRound) : Number(roundFilter);

    if (Number.isFinite(requestedRound) && requestedRound > 0) {
        return requestedRound;
    }

    return rounds.length ? Math.max(...rounds) : null;
};

const getAdmin1Name = (row) =>
    row?.admin_1_name ||
    row?.admin1_name ||
    row?.admin_1 ||
    row?.admin1 ||
    row?.admin_1_name_en;

const getAdmin2Name = (row) =>
    row?.admin_2_name ||
    row?.admin2_name ||
    row?.admin_2 ||
    row?.admin2 ||
    row?.admin_2_name_en;

const getRowCountry = (row) =>
    row?.admin_0_name ||
    row?.adm0_0_name ||
    row?.country ||
    row?.Country;

const getReasonLabels = (value) => {
    const labels = String(value || "")
        .split(/[,;/|]+/)
        .map((item) => {
            const label = item.trim();
            return /^No reason for displacement reported$/i.test(label)
                ? "No reason reported"
                : label;
        })
        .filter(Boolean);
    return labels.length ? labels : ["Unspecified"];
};

const getMetricValue = (row, populationType) => {
    if (populationType === "male") {
        return toNumber(row?.maleidps ?? row?.male_idps ?? row?.male);
    }
    if (populationType === "female") {
        return toNumber(row?.femaleidps ?? row?.female_idps ?? row?.female);
    }
    return toNumber(row?.idps ?? row?.total_idps ?? row?.value);
};

const RoundReason = () => {
    const selectedCountry = useSelector((state) => state.selection.country);
    const selectedAdmin1 = useSelector((state) => state.selection.admin1);
    const selectedAdmin2 = useSelector((state) => state.selection.admin2);
    const selectedRound = useSelector((state) => state.selection.round);
    const selectedRoundFilter = useSelector((state) => state.selection.roundFilter);
    const selectedYear = useSelector((state) => state.selection.year);
    const selectedOperation = useSelector((state) => state.selection.operation);
    const populationType = useSelector((state) => state.selection.populationType);
    const countryRows = useSelector((state) => state.agg.country_Agg || []);
    const admin1Rows = useSelector((state) => state.agg.admin1 || []);
    const admin2Rows = useSelector((state) => state.agg.admin2 || []);

    const reasonRows = useMemo(() => {
        const source = selectedAdmin1 || selectedAdmin2
            ? admin2Rows
            : selectedCountry
                ? admin1Rows
                : countryRows;
        const selectedYearNumber = Number(selectedYear);
        const hasSelectedYear = Number.isFinite(selectedYearNumber);
        const effectiveOperation =
            selectedOperation && selectedOperation !== "All" ? selectedOperation : "";
        const countryName = normalizeName(selectedCountry);
        const admin1Name = normalizeName(selectedAdmin1);
        const admin2Name = normalizeName(selectedAdmin2);
        const grouped = new Map();

        const passesBaseFilters = (row) => {
            if (hasSelectedYear && Number(row.year) !== selectedYearNumber) return;

            const operation = row.operation || row.Operation || row.operation_name;
            if (effectiveOperation && operation !== effectiveOperation) return;

            const rowCountry = normalizeName(getRowCountry(row));
            if (countryName && rowCountry && rowCountry !== countryName) return;
            if (admin1Name && normalizeName(getAdmin1Name(row)) !== admin1Name) return;
            if (admin2Name && normalizeName(getAdmin2Name(row)) !== admin2Name) return;

            return true;
        };

        const baseRows = (source || []).filter(passesBaseFilters);
        const targetRound = resolveTargetRound(baseRows, selectedRoundFilter, selectedRound);

        baseRows.forEach((row) => {
            if (targetRound != null && getRound(row) !== targetRound) return;

            const value = getMetricValue(row, populationType);
            if (value <= 0) return;

            const reasons = getReasonLabels(row.reason_for_displacement);
            const valuePerReason = value / reasons.length;
            reasons.forEach((reason) => {
                grouped.set(reason, (grouped.get(reason) || 0) + valuePerReason);
            });
        });

        return Array.from(grouped.entries())
            .map(([label, value]) => ({ label, value }))
            .sort((a, b) => b.value - a.value);
    }, [
        admin1Rows,
        admin2Rows,
        countryRows,
        populationType,
        selectedAdmin1,
        selectedAdmin2,
        selectedCountry,
        selectedOperation,
        selectedRound,
        selectedRoundFilter,
        selectedYear,
    ]);

    if (!selectedCountry || !reasonRows.length) return null;

    const maxValue = Math.max(...reasonRows.map((row) => row.value));
    const totalValue = reasonRows.reduce((sum, row) => sum + row.value, 0);
    const metricLabel =
        populationType === "male"
            ? "Male IDPs"
            : populationType === "female"
                ? "Female IDPs"
                : "IDPs";

    return (
        <div className={classes.box_other}>
            <div className={classes.round_reason_box}>
                <div className={classes.round_reason_header}>
                    <p className={classes.idps_box_title}>Reasons for displacement</p>
                    <span className={classes.round_reason_metric}>{metricLabel}</span>
                </div>
                <div className={classes.idps_section_separator} />
                <div className={classes.round_reason_list}>
                    {reasonRows.map((row) => {
                        const width = maxValue > 0 ? `${(row.value / maxValue) * 100}%` : "0%";
                        return (
                            <div className={classes.round_reason_item} key={row.label}>
                                <div className={classes.round_reason_row}>
                                    <span className={classes.round_reason_label}>{row.label}</span>
                                    <span className={classes.round_reason_value}>
                                        {formatNumber(row.value)}
                                        <span className={classes.round_reason_percent}>
                                            {formatPercent(row.value, totalValue)}
                                        </span>
                                    </span>
                                </div>
                                <div className={classes.round_reason_track} aria-hidden="true">
                                    <span
                                        className={classes.round_reason_bar}
                                        style={{ width }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default RoundReason;
